/**
 * Surface registry (OPSFL-HELP-1, lane 1a) — the ONE route→surface map the help system keys off.
 * The surface ids are the CLOSED contract list (docs/design/OPSFL-HELP-1-contract.md listed the
 * original 14; `alerts` is the 15th, added with its surface by OPSFL-ALUI-1, `case-detail` the 16th
 * with OL-26, `bulk` and `bulk-batch` the 17th and 18th with OL-29 — a new surface ships
 * WITH its registry row, content doc and glossary terms, or the coverage/drift gates fail). The
 * route column below is corrected against App.tsx, which is implementation truth:
 *   - the work queue lives at `/workbench` (`/` merely redirects there);
 *   - the designer lives at `/flows/:flowId` (incl. `/flows/new`) — there is no `/designer` route;
 *   - entity detail is `/entities/:entityId`.
 * `edu/tourModel.ts` derives its tour-surface tags from this registry (single source of truth for
 * route parsing; the tour TAGS stay unchanged because seeded tour data is scoped by them).
 */

export const SURFACE_IDS = [
  'workbench',
  'runs',
  'bulk',
  'bulk-batch',
  'case-detail',
  'entities',
  'entity-detail',
  'flows',
  'designer',
  'templates',
  'screens',
  'simulations',
  'migration-review',
  'oversight',
  'integrations',
  'analytics',
  'alerts',
  'admin',
] as const;

export type SurfaceId = (typeof SURFACE_IDS)[number];

export interface SurfaceEntry {
  surfaceId: SurfaceId;
  /** The route pattern(s) as mounted in App.tsx (implementation truth, not the contract draft). */
  routes: string[];
  page: string;
}

/** The registry table — used by the coverage gate and as the reference for both lanes. */
export const SURFACES: readonly SurfaceEntry[] = [
  { surfaceId: 'workbench', routes: ['/workbench', '/'], page: 'WorkbenchPage' },
  { surfaceId: 'runs', routes: ['/runs'], page: 'RunsPage' },
  { surfaceId: 'bulk', routes: ['/bulk'], page: 'BulkPage' },
  { surfaceId: 'bulk-batch', routes: ['/bulk/:batchId'], page: 'BulkBatchPage' },
  { surfaceId: 'case-detail', routes: ['/cases/:runId'], page: 'CaseDetailPage' },
  { surfaceId: 'entities', routes: ['/entities'], page: 'EntitiesPage' },
  { surfaceId: 'entity-detail', routes: ['/entities/:entityId'], page: 'EntityDetailPage' },
  { surfaceId: 'flows', routes: ['/flows'], page: 'FlowsListPage' },
  { surfaceId: 'designer', routes: ['/flows/:flowId', '/flows/new'], page: 'DesignerPage' },
  { surfaceId: 'templates', routes: ['/templates'], page: 'TemplatesPage' },
  { surfaceId: 'screens', routes: ['/screens'], page: 'ScreenStudioPage' },
  { surfaceId: 'simulations', routes: ['/simulations'], page: 'SimulationsPage' },
  { surfaceId: 'migration-review', routes: ['/migration'], page: 'MigrationReviewPage' },
  { surfaceId: 'oversight', routes: ['/oversight'], page: 'OversightPage' },
  { surfaceId: 'integrations', routes: ['/integrations'], page: 'IntegrationsPage' },
  { surfaceId: 'analytics', routes: ['/analytics'], page: 'AnalyticsPage' },
  { surfaceId: 'alerts', routes: ['/alerts'], page: 'AlertsPage' },
  { surfaceId: 'admin', routes: ['/admin'], page: 'AdminPage' },
];

/**
 * Map an app pathname → its help surface. Total function: unknown paths resolve to `workbench`,
 * mirroring the app-level catch-all (`*` → /workbench) so the panel is never surface-less.
 */
export function surfaceForPath(pathname: string): SurfaceId {
  const segments = pathname.split('/').filter(Boolean);
  switch (segments[0]) {
    case undefined:
      return 'workbench'; // '/' — index redirect target
    case 'workbench':
      return 'workbench';
    case 'runs':
      return 'runs';
    case 'bulk':
      return segments.length > 1 ? 'bulk-batch' : 'bulk';
    case 'cases':
      return segments.length > 1 ? 'case-detail' : 'runs';
    case 'entities':
      return segments.length > 1 ? 'entity-detail' : 'entities';
    case 'flows':
      // `/flows` is the list; anything deeper (`/flows/:flowId`, `/flows/new`) is the designer.
      return segments.length > 1 ? 'designer' : 'flows';
    case 'templates':
      return 'templates';
    case 'screens':
      return 'screens';
    case 'simulations':
      return 'simulations';
    case 'migration':
      return 'migration-review';
    case 'oversight':
      return 'oversight';
    case 'integrations':
      return 'integrations';
    case 'analytics':
      return 'analytics';
    case 'alerts':
      return 'alerts';
    case 'admin':
      return 'admin';
    default:
      return 'workbench'; // app catch-all redirects here too
  }
}
